import { Button } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Tooltip } from '@televet/kibble-ui/Tooltip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { LinkText } from '@/components/LinkText';
import { CopyableId } from '@/components/CopyableId';
import { JsonPanel } from '@/components/JsonPanel';
import { PageContainer } from '@/components/PageContainer';
import { Field } from '@/components/Field';
import { SummaryCard } from '@/components/SummaryCard';
import { trpc, trpcClient } from '@/lib/trpc';
import { modeVariant, runStatusVariantMap } from '@/features/workflows/badgePalettes';
import { namespacePalette } from '@/lib/dataSourceNamespace';
import { RunTaskCard, type RunTask } from '@/features/workflows/components/RunTaskCard';
import { PendingTaskCard } from '@/features/workflows/components/PendingTaskCard';
import { RunCampaignOptOutsSection } from '@/features/workflows/components/RunCampaignOptOutsSection';
import { activeOptOutChannels, buildRunTaskSequence } from '@/features/workflows/utils';
import {
  FormResponseCard,
  type RunFormResponse,
} from '@/features/workflows/components/FormResponseCard';

// Must stay in sync with TERMINAL_RUN_STATUSES in packages/workflow/src/constants.ts
/** One string field out of a run's JSON data, or undefined if it is not one. */
const readStringField = (data: unknown, key: string): string | undefined => {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return undefined;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
};

/**
 * The data source a run recorded, read from either shape: some emitters write a flat
 * `dataSourceNamespace`, others a nested `dataSource` with a `connection` beside it.
 *
 * Absent for most runs, and absence is NOT Owl. `extractDataSourceNamespace` in the engine
 * defaults to Owl when the key is missing, so printing that default here would state as fact
 * something the run never recorded, and would be wrong for any unrecorded run that read Yak.
 * Nothing is shown unless the run said so itself.
 */
const readDataSource = (data: unknown): { namespace?: string; connection?: string } => {
  const record = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
  const nested =
    typeof record.dataSource === 'object' && record.dataSource !== null
      ? (record.dataSource as Record<string, unknown>)
      : {};
  const pick = (value: unknown): string | undefined =>
    typeof value === 'string' && value !== '' ? value : undefined;
  return {
    namespace: pick(record.dataSourceNamespace) ?? pick(nested.namespace),
    connection: pick(nested.connection),
  };
};

const TERMINAL_RUN_STATUSES = new Set(['Completed', 'Suppressed', 'Killed', 'Errored']);

/**
 * How often the page refetches while live refresh is on. Fast enough to watch a
 * run progress, slow enough that leaving the tab open is not a load problem.
 */
const LIVE_REFRESH_MS = 5000;

/** Kibble's Tooltip defaults to 750ms, long enough to feel like nothing happened. */
const TOOLTIP_DELAY_MS = 200;

const RouteComponent = () => {
  const { runId } = Route.useParams();
  const queryClient = useQueryClient();
  const [confirmSuppress, setConfirmSuppress] = useState(false);
  const [suppressError, setSuppressError] = useState<string | null>(null);

  const [liveRefresh, setLiveRefresh] = useState(false);

  const { data } = useQuery({
    ...trpc.workflow.getRunDetail.queryOptions({ runId }),
    // The function form reads the status off the query itself: referencing
    // `data` here would be a circular initializer. Stops on its own once the run
    // reaches a terminal status, since nothing more will change.
    refetchInterval: (query) =>
      liveRefresh && !TERMINAL_RUN_STATUSES.has(query.state.data?.run?.status ?? '')
        ? LIVE_REFRESH_MS
        : false,
  });

  const suppressMutation = useMutation({
    mutationFn: () => trpcClient.workflow.suppressRun.mutate({ runId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.workflow.getRunDetail.queryOptions({ runId }).queryKey,
      });
      setConfirmSuppress(false);
      setSuppressError(null);
    },
    onError: (error) => {
      setConfirmSuppress(false);
      setSuppressError(error instanceof Error ? error.message : 'Failed to suppress run');
      setTimeout(() => setSuppressError(null), 5000);
    },
  });

  const run = data?.run;

  // Tasks arrive ordered by createdAt. `activateStep` creates a step's task rows
  // when it activates that step, so across steps this is the order the run
  // actually took, including which branch it took. Tasks within one step are
  // created together and have no order between them.
  const tasks: RunTask[] = run?.runTasks ?? [];
  const intentTotal = tasks.reduce((total, task) => total + task.messageIntents.length, 0);

  // Every task the workflow defines, with this run's tasks in place. A run only creates a
  // task once its step activates, so without this the list starts empty and grows, and what
  // is still ahead of the run is invisible.
  const taskSlots = buildRunTaskSequence(tasks, run?.workflow?.steps ?? []);

  const formResponses: RunFormResponse[] = data?.formResponses ?? [];
  const campaignOptOuts = data?.campaignOptOuts ?? null;
  const optedOutChannels =
    campaignOptOuts?.status === 'checked' ? activeOptOutChannels(campaignOptOuts.optOuts) : [];

  // Whether a pet parent figures in this run at all, which is what decides if the pet parent
  // funnel means anything. Recipient as well as assignee: the Hills nutrition intake reaches a
  // pet parent through a System task, so an assignee-only test would hide it there, while its
  // clinic digest sibling reaches an InformUser through the same shape and must stay hidden.
  const hasPetParentInvolvement = tasks.some(
    (task) =>
      task.assigneeType === 'ClinicPetParent' ||
      task.messageIntents.some((intent) => intent.recipientType === 'ClinicPetParent')
  );

  if (!run) {
    return (
      <PageContainer>
        <Flex justify="center" align="center" p={8}>
          <Spinner size="lg" />
        </Flex>
      </PageContainer>
    );
  }

  const canSuppress = !TERMINAL_RUN_STATUSES.has(run.status);

  // The emitter execution this run came from. The event processor stamps it into
  // run data as `emitterExecutionId`; there is no column for it, so this is the
  // only place it exists.
  const emitterExecutionId = readStringField(run.data, 'emitterExecutionId');

  const dataSource = readDataSource(run.data);

  return (
    <PageContainer>
      <Flex direction="column" gap={4} width="100%">
        <Breadcrumbs
          items={[
            { label: 'Workflows', to: '/workflows' },
            { label: 'Runs', to: '/workflow-runs' },
            // Full id, unprefixed: the crumb is where you copy the id from,
            // and a truncated one cannot be used for that.
            { label: runId },
          ]}
        />

        <Flex align="center" gap={3} wrap="wrap">
          <Text fontSize="2xl" fontWeight="bold">
            Run Detail
          </Text>
          <Tag
            label={run.status}
            variant={runStatusVariantMap[run.status] ?? 'default'}
            size="lg"
          />
          <Tag label={run.mode} variant={modeVariant(run.mode)} size="lg" />
          {canSuppress &&
            (confirmSuppress ? (
              <Flex gap={2} ml="auto">
                <Button size="sm" variant="ghostNeutral" onClick={() => setConfirmSuppress(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primaryDestructive"
                  loading={suppressMutation.isPending}
                  onClick={() => suppressMutation.mutate()}
                  data-testid="run-suppress-confirm"
                >
                  Suppress run
                </Button>
              </Flex>
            ) : (
              <Button
                size="sm"
                variant="secondaryDestructive"
                ml="auto"
                onClick={() => setConfirmSuppress(true)}
                data-testid="run-suppress"
              >
                Suppress run
              </Button>
            ))}
          {suppressError && (
            <Text fontSize="sm" variant="danger">
              {suppressError}
            </Text>
          )}
          {/* Only offered while the run can still change. `ml="auto"` when it is
              the only action, so it still sits at the right of the row. */}
          {canSuppress && (
            <Tooltip label="Auto-refresh" openDelay={TOOLTIP_DELAY_MS} placement="top">
              <Button
                size="sm"
                variant="secondary"
                iconName="arrowCircle"
                active={liveRefresh}
                // The spin is the whole "it is on" signal; 2s because the `slowest` token reads as frantic.
                css={{
                  '& svg': { animation: liveRefresh ? 'spin' : undefined, animationDuration: '2s' },
                }}
                ml={confirmSuppress ? undefined : 0}
                aria-label={liveRefresh ? 'Turn off auto-refresh' : 'Turn on auto-refresh'}
                onClick={() => setLiveRefresh(!liveRefresh)}
                data-testid="run-live-refresh"
              />
            </Tooltip>
          )}
        </Flex>

        <Flex gap={4} wrap="wrap">
          <SummaryCard title="RUN">
            <Field label="Workflow">
              {run.workflow ? (
                /* A real anchor, so middle-click and open-in-new-tab work.
                   Hover underline matches how Breadcrumbs marks a link. */
                <Link
                  to="/workflows/$workflowId"
                  params={{ workflowId: run.workflow.id }}
                  style={{ textDecoration: 'none' }}
                >
                  <LinkText fontSize="sm" fontWeight="medium" data-testid="run-workflow-link">
                    {run.workflow.name}
                  </LinkText>
                </Link>
              ) : (
                <Text fontSize="sm">—</Text>
              )}
            </Field>
            <Field label="Config">
              <Text fontSize="sm">
                {run.workflowConfiguration.name ??
                  run.workflowConfiguration.ownerType ??
                  'Platform'}
              </Text>
            </Field>
            <Field label="Run ID" minW="260px">
              <CopyableId id={run.id} label="run" data-testid="run-id" />
            </Field>
            {/* Only when the run recorded one. Most runs record neither, and the engine's Owl
                default is a fallback, not something this run stated. */}
            {(dataSource.namespace || dataSource.connection) && (
              <Field label="Data source">
                <Flex align="center" gap={1} wrap="wrap">
                  {/* Same variant the executions table gives a namespace, so a run and the
                      emitter execution behind it read as the same cluster. */}
                  {dataSource.namespace && (
                    <Tag
                      label={dataSource.namespace}
                      variant={namespacePalette(dataSource.namespace)}
                      size="sm"
                      data-testid="run-data-source-namespace"
                    />
                  )}
                  {dataSource.connection && (
                    <Tag
                      label={dataSource.connection}
                      variant="default"
                      size="sm"
                      data-testid="run-data-source-connection"
                    />
                  )}
                </Flex>
              </Field>
            )}
          </SummaryCard>

          <SummaryCard title="DETAILS">
            <Field label="Tasks">
              {/* Created against defined, once the run has not reached them all. */}
              <Text fontSize="sm">
                {taskSlots.length > tasks.length
                  ? `${tasks.length} of ${taskSlots.length}`
                  : tasks.length}
              </Text>
            </Field>
            <Field label="Message intents">
              <Text fontSize="sm">{intentTotal}</Text>
            </Field>
            {formResponses.length > 0 && (
              <Field label="Forms">
                <Text fontSize="sm">{formResponses.length}</Text>
              </Field>
            )}
          </SummaryCard>

          <SummaryCard title="TRIGGER">
            <Field label="Entity">
              {run.entityType ? (
                <Tag label={run.entityType} variant="default" size="sm" />
              ) : (
                <Text fontSize="sm">—</Text>
              )}
            </Field>
            <Field label="Entity ID" minW="260px">
              {run.entityId ? (
                <CopyableId id={run.entityId} label="entity" maxW="220px" />
              ) : (
                <Text fontSize="sm">—</Text>
              )}
            </Field>
            {/* A processor id has no spaces to wrap on, so it used to push past the card. */}
            <Field label="Triggered by" minW="0">
              {/* Full value on hover, because the tail is the part that identifies it. */}
              <Text fontSize="sm" maxW="100%" truncated title={run.triggeredBy ?? undefined}>
                {run.triggeredBy ?? '—'}
              </Text>
            </Field>
            {/* The emitter execution that produced this run, recorded by the
                event processor in the run's own data. Absent for a run started
                any other way — a manual trigger, or an emitter old enough to
                predate the field. */}
            {emitterExecutionId && (
              <Field label="Execution">
                <Link
                  to="/emitters/executions/$executionId"
                  params={{ executionId: emitterExecutionId }}
                >
                  <LinkText fontSize="sm">View</LinkText>
                </Link>
              </Field>
            )}
            <Field label="Created">
              <Text fontSize="sm">{new Date(run.createdAt).toLocaleString()}</Text>
            </Field>
          </SummaryCard>

          {/* Funnel stamps — set by the completion flags this run's task
              definitions carry (CU-86e2ykq11). "Not yet" is a real state,
              not missing data: an unstamped run is what a misconfigured
              flag looks like.

              Shown when a pet parent is involved or an Inform opt-out check
              applies. A clinic digest run reaches an InformUser through a System
              task, so an ungated card offered a funnel for a run with no pet parent. */}
          {(hasPetParentInvolvement || campaignOptOuts !== null) && (
            <SummaryCard title="PET PARENT">
              <Field label="Started">
                <Text fontSize="sm" data-testid="run-first-pet-parent-interaction-at">
                  {run.firstPetParentInteractionAt
                    ? new Date(run.firstPetParentInteractionAt).toLocaleString()
                    : 'Not yet'}
                </Text>
              </Field>
              <Field label="Completed">
                <Text fontSize="sm" data-testid="run-pet-parent-completed-at">
                  {run.petParentCompletedAt
                    ? new Date(run.petParentCompletedAt).toLocaleString()
                    : 'Not yet'}
                </Text>
              </Field>
              {/* Omitted when no Inform check applies (no campaign or no recipient), since a
                  dash would claim one ran. Per channel: an SMS opt-out does not stop email. */}
              {campaignOptOuts && (
                <Field label="Opted out">
                  {campaignOptOuts.status === 'unavailable' ? (
                    <Text fontSize="sm" variant="subtle" data-testid="run-pet-parent-opted-out">
                      Couldn't check
                    </Text>
                  ) : optedOutChannels.length > 0 ? (
                    <Flex gap={1} data-testid="run-pet-parent-opted-out">
                      {optedOutChannels.map((channel) => (
                        <Tag key={channel} label={channel} variant="error" size="sm" />
                      ))}
                    </Flex>
                  ) : (
                    <Text fontSize="sm" data-testid="run-pet-parent-opted-out">
                      —
                    </Text>
                  )}
                </Field>
              )}
            </SummaryCard>
          )}
        </Flex>

        <JsonPanel label="Run data" data={run.data} data-testid="run-data" />

        <Flex direction="column" gap={2}>
          <Text fontSize="lg" fontWeight="semibold">
            Tasks
          </Text>

          {taskSlots.length === 0 ? (
            <Card size="sm" py={6}>
              <Text fontSize="sm" variant="subtle" textAlign="center">
                No tasks — this run created none, and its workflow defines none.
              </Text>
            </Card>
          ) : (
            <Flex direction="column" gap={2}>
              {taskSlots.map((slot) =>
                slot.kind === 'run' ? (
                  <RunTaskCard
                    key={slot.task.id}
                    task={slot.task}
                    position={slot.position}
                    isCurrentStep={slot.task.workflowStep.id === run.currentStep?.id}
                    runId={runId}
                  />
                ) : (
                  <PendingTaskCard
                    key={slot.definition.id}
                    definition={slot.definition}
                    step={slot.step}
                  />
                )
              )}
            </Flex>
          )}
        </Flex>

        {/* Only when the run has forms. Most workflows send messages and never mint one, and an
            empty panel on every run would be noise rather than an empty state worth reading. */}
        {formResponses.length > 0 && (
          <Flex direction="column" gap={2}>
            <Text fontSize="lg" fontWeight="semibold">
              Forms
            </Text>
            <Flex direction="column" gap={2}>
              {formResponses.map((response) => (
                <FormResponseCard key={response.id} response={response} />
              ))}
            </Flex>
          </Flex>
        )}

        <RunCampaignOptOutsSection campaignOptOuts={campaignOptOuts} />
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/workflow-runs/$runId')({
  component: RouteComponent,
});
